/**
 * Scene-save bridge — lets DOM/instance-agnostic tools (the MCP `save_scene`
 * tool) persist the current scene without reaching into the imperative
 * `TraceSandbox` instance or `document.querySelector`.
 *
 * WHY THIS EXISTS
 * ---------------
 * `commit_behavior` / `add_behavior` / `wire_group_state` edits are RUNTIME-ONLY:
 * they mutate the live behavior tree but do NOT persist. On the next asset reload
 * they silently revert — which bit us repeatedly (swatch graphs and layout-button
 * BehaviorGraphs both vanished). There was no headless way to persist, because the
 * only save surfaces are the imperative TraceSandbox methods (CMS publish / asset
 * PATCH / file download), none reachable from a ToolContext (which holds only the
 * TraceScene + optional viewer element).
 *
 * `TraceSandbox` registers a saver here on construction; `save_scene` calls it.
 * Save is deliberately EXPLICIT — it PATCHes/publishes/downloads (outward-facing),
 * so nothing auto-saves; the tool only persists when the user/agent asks.
 */

import type { EditorSession } from 'trace-viewer/editor';

/**
 * Whether the current scene has unsaved edits since the last save/load, or
 * null when there is no saved copy to compare with (a new scene).
 */
export type DirtyProbe = () => boolean | null;

/**
 * Persist the current scene in place using whatever route fits the load mode
 * (CMS publish, source-asset PATCH, or file download). Resolves with a short
 * human-readable summary of what happened; rejects on failure.
 */
export type SceneSaver = () => Promise<string>;

let activeSaver: SceneSaver | null = null;
let activeDirtyProbe: DirtyProbe | null = null;

/**
 * Register the sandbox's save routine + dirty probe. Called once by TraceSandbox.
 * Replaces any prior registration (a fresh sandbox owns the bridge).
 */
export function registerSceneSaver(saver: SceneSaver, dirtyProbe: DirtyProbe): void {
    activeSaver = saver;
    activeDirtyProbe = dirtyProbe;
}

/** Tear down the registration (sandbox dispose). */
export function unregisterSceneSaver(): void {
    activeSaver = null;
    activeDirtyProbe = null;
}

/** The registered saver, or null in headless/no-sandbox contexts. */
export function getSceneSaver(): SceneSaver | null {
    return activeSaver;
}

/**
 * Whether the scene has unsaved edits. Returns null when no probe is registered
 * (headless / unknown) so callers can distinguish "clean" from "can't tell".
 */
export function isSceneDirty(): boolean | null {
    return activeDirtyProbe ? activeDirtyProbe() : null;
}

/**
 * The dirty probe for an editor session. A session with no source asset has
 * never been saved and calls itself clean whatever was edited, so it answers
 * null ("can't tell") and a save goes ahead. CMS mode always opens the session
 * with the scene id, so a CMS scene has a source asset.
 */
export function sessionDirtyProbe(session: Pick<EditorSession, 'sourceAsset' | 'isDirty'>): DirtyProbe {
    return () => (session.sourceAsset ? session.isDirty : null);
}
